import { type ThemeOptions } from '@mui/material/styles';

type ColorPalette = ThemeOptions['colors'];

const background: ColorPalette['background'] = {
    'interactive-bold': '#350359',
    'interactive-subtle': '#EBE4F0',
    'interactive-subtle-hover': '#C1ABD2',
    'interactive-subtle-disabled': '#F0EFF0',
    'accent-bold': '#8A00CF',
    'accent-subtle': '#F3E4FC',
    'accent-subtle-hover': '#E8CCF5',
    'page-default': '#FBF9F9',
    subtle: '#FAFAFA',
    'subtle-hover': '#EBEAEB',
    stronger: '#EBEAEB',
    'stronger-hover': '#DEDDDF',
    'stronger-pressed': '#D4D2D5',
    'progress-bold': '#007485',
    'progress-subtle': '#F0F8FA',
    'progress-star-not-achieved': '#FDE9F0',
    'progress-star-achieved': '#F46E9B',
    'progress-star-gold': '#F9B444',
    'success-bold': '#007C3E',
    'success-bold-standalone': '#008844',
    'success-bold-hover': '#006130',
    'success-subtle': '#F4FAF7',
    'success-subtle-hover': '#C7E5D6',
    'success-subtle-pressed': '#8AC8A9',
    'warning-bold': '#B85600',
    'warning-bold-standalone': '#D36B10',
    'warning-bold-hover': '#8B4100',
    'warning-subtle': '#FEFAF5',
    'warning-subtle-hover': '#F7D5B7',
    'warning-subtle-pressed': '#F5B67F',
    'danger-bold': '#BA1C24',
    'danger-bold-hover': '#971717',
    'danger-subtle': '#FCEDEE',
    'danger-subtle-hover': '#F2BABD',
    'danger-subtle-pressed': '#EB989C',
    'practice-correct': '#C7E5D6',
    'practice-corrected': '#8AC8A9',
    'practice-incorrect': '#F2BABD',
    default: '#FFFFFF',
    'default-disabled': '#F0EFF0',
    'default-hover': '#F8F6F6',
    'default-hover(alpha)': '#E7DFE0',
    'classroom-default': '#160125',
    'classroom-default-subtle': '#332D39',
    'classroom-tool-default': '#26023F',
};

const border: ColorPalette['border'] = {
    strong: '#DEDDDF',
    'strong-hover': '#D4D2D5',
    stronger: '#A19EA4',
    'stronger-hover': '#77727B',
    'interactive-bold': '#58287B',
    'interactive-subtle': '#CEBDDB',
    'interactive-subtle-hover': '#A383BB',
    'interactive-subtle-disabled': '#C0BEC2',
    'progress-bold': '#0E7E90',
    'progress-subtle': '#D9EFF2',
    'accent-bold': '#7E00BC',
    'accent-subtle': '#E8CCF5',
    'danger-bold': '#D42029',
    'danger-bold-pressed': '#751217',
    'danger-subtle': '#F2BABD',
    'success-bold': '#007C3E',
    'success-bold-pressed': '#004B25',
    'success-subtle': '#C7E5D6',
    'warning-bold': '#B85600',
    'warning-bold-pressed': '#723603',
    'warning-subtle': '#F7D5B7',
    inverse: '#FFFFFF',
};

const brand: ColorPalette['brand'] = {
    beige: '#F4E9EA',
    neon: '#D5FD44',
    'neon-stronger': '#C2E63E',
    plum: '#1B0338',
    'pink-light': '#FEF6FC',
    turquoise: '#0F8B9E',
    'turquoise-subtle': '#D9EFF2',
    purple: '#AE00FF',
    'purple-subtle': '#EFCCFF',
    pink: '#F8A1E2',
    'pink-subtle': '#FDE2F6',
    coral: '#EF2669',
};

const text: ColorPalette['text'] = {
    default: '#1B0338',
    'default-disabled': '#A19EA4',
    subtle: '#5C5661',
    accent: '#8A00CF',
    'accent-bold-hover': '#7E00BC',
    'accent-disabled': '#C0BEC2',
    interactive: '#58287B',
    'interactive-disabled': '#C0BEC2',
    success: '#007C3E',
    danger: '#BA1C24',
    warning: '#B85600',
    progress: '#007485',
    'gender-feminine': '#D92360',
    'gender-masculine': '#0E7E90',
    'gender-neutral': '#5C6F18',
    inverse: '#FFFFFF',
    inverseSubtle: '#B3B3B3',
    'interactive-inverse-hover': '#E8CCF5',
    'interactive-inverse-pressed': '#D4A3E8',
    'interactive-inverse-disabled': '#9E9EA0',
    'accent-inverse-disabled': '#9E9EA0',
};

/**
 * @internal Exported only for theme configuration purposes.
 * Prefer MUI semantic tokens (e.g. `theme.palette.*`) over `theme.colors.*`
 * when a mapping exists — see type definitions in `models.ts`.
 */
export const colors: ColorPalette = {
    background,
    border,
    brand,
    text,
};
